/* src/pages/Proof.jsx
 * Workspace conversions log + ad-platform connection status (read-only).
 * Data: trackingApi.getConversionLog, trackingApi.getLeadSources, googleAdsApi.status
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { trackingApi, googleAdsApi } from '../utils/api';
import { dealStageLabel } from '../components/inbox/chips';
import { formatMoney } from '../utils/todayStats';
import { ProofListSkeleton } from '../components/Skeleton';

function formatCustomerId(id) {
  if (!id) return '';
  const digits = String(id).replace(/\D/g, '');
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return id;
}

function formatPlatformLabel(platform) {
  return String(platform || 'unknown').replace(/_/g, ' ').toUpperCase();
}

function outcomeChipClass(outcome) {
  if (outcome === 'accepted' || outcome === 'sent') return 'accent-soft';
  if (outcome === 'skipped') return 'danger';
  if (outcome === 'failed') return 'warn';
  return '';
}

function leadDisplayName(event) {
  return event.leadName || event.leadEmail || 'Unknown lead';
}

function formatProcessedAt(iso) {
  if (!iso) return 'N/A';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function OutcomeChips({ outcomes }) {
  if (!Array.isArray(outcomes) || outcomes.length === 0) {
    return <span className="proof-row-muted">No outcomes recorded</span>;
  }
  return (
    <div className="proof-row-outcomes">
      {outcomes.map((o, idx) => {
        const label = `${formatPlatformLabel(o.platform)} \u00b7 ${String(o.outcome || 'unknown').toUpperCase()}`;
        return (
          <span
            key={`${o.platform}-${idx}`}
            className={`inbox-chip ${outcomeChipClass(o.outcome)}`}
            style={{ padding: '4px 8px' }}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

function ConnectionPanel({ adsStatus, adsLoading }) {
  return (
    <div className="today-panel proof-connections-panel">
      <div className="today-panel-header">
        <span className="today-panel-title">Connections</span>
      </div>
      <div className="proof-connections-body">
        <div className="proof-connection-row">
          <span className="proof-connection-label">Google Ads</span>
          {adsLoading ? (
            <span className="proof-row-muted">Loading status...</span>
          ) : adsStatus?.status === 'connected' || adsStatus?.connected ? (
            <div className="proof-connection-detail">
              <span className="inbox-chip accent-soft">Connected</span>
              <span className="proof-connection-name">
                {adsStatus.accountDescriptiveName || formatCustomerId(adsStatus.customerId) || 'Connected account'}
              </span>
            </div>
          ) : adsStatus?.status === 'pending' ? (
            <div className="proof-connection-detail">
              <span className="inbox-chip warn">Connecting</span>
              <span className="proof-row-muted">Finish setup in Team &amp; integrations.</span>
            </div>
          ) : (
            <div className="proof-connection-detail">
              <span className="inbox-chip">Not connected</span>
              <p className="proof-connection-note">
                Connect Google Ads to send Won deals back as conversions.{' '}
                <Link to="/advanced-settings" className="today-link-accent">
                  Team &amp; integrations &rarr;
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatLeadPct(leads, totalLeads) {
  if (!totalLeads) return '0%';
  return `${Math.round((leads / totalLeads) * 100)}%`;
}

function LeadSourcesPanel({ channels, totals, loading, error, onRefresh }) {
  return (
    <div className="today-panel proof-sources-panel">
      <div className="today-panel-header">
        <div>
          <span className="today-panel-title">Lead sources</span>
          <p className="proof-sources-subtitle">
            Where leads and revenue came from — captured from click IDs, UTMs, and referrer on each submission.
            Not GA4; includes organic, referral, direct, and email.
          </p>
        </div>
      </div>

      {error && (
        <div className="today-load-error" style={{ margin: '14px 20px 0' }}>
          Could not load lead sources. Refresh to retry.{' '}
          <button type="button" className="inbox-pill-btn" onClick={onRefresh} style={{ marginLeft: '8px' }}>
            Refresh
          </button>
        </div>
      )}

      {loading ? (
        <ProofListSkeleton />
      ) : !error && channels.length === 0 ? (
        <div className="today-empty">
          No lead source data yet. Attribution is captured when someone submits a form or starts a chat.
        </div>
      ) : (
        !error && (
          <div className="proof-sources-body">
            <table className="proof-sources-table">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Leads</th>
                  <th>%</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {channels.map((row) => (
                  <tr key={row.channel}>
                    <td>
                      <span className="proof-sources-channel">{row.label}</span>
                      {row.platform && (
                        <span className="proof-sources-platform">{row.platform}</span>
                      )}
                    </td>
                    <td>
                      <div className="proof-sources-leads-cell">
                        <span className="proof-sources-num">{row.leads}</span>
                        <div className="proof-sources-bar-track">
                          <div
                            className="proof-sources-bar-fill"
                            style={{
                              width: totals.leads
                                ? `${Math.max(2, (row.leads / totals.leads) * 100)}%`
                                : '0%',
                            }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="proof-sources-num">{formatLeadPct(row.leads, totals.leads)}</td>
                    <td className="proof-sources-num">{formatMoney(row.revenue) || '$0'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>Total</strong></td>
                  <td className="proof-sources-num"><strong>{totals.leads}</strong></td>
                  <td className="proof-sources-num"><strong>100%</strong></td>
                  <td className="proof-sources-num"><strong>{formatMoney(totals.revenue) || '$0'}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </div>
  );
}

function ConversionRow({ event }) {
  const valueLabel = formatMoney(event.dealValue);
  const name = leadDisplayName(event);

  return (
    <div className="proof-row">
      <div className="proof-row-top">
        <div className="proof-row-meta">
          <span className="proof-row-date">{formatProcessedAt(event.processedAt)}</span>
          <span className="proof-row-lead">{name}</span>
          {event.leadName && event.leadEmail && (
            <span className="proof-row-email mono">{event.leadEmail}</span>
          )}
        </div>
        <div className="proof-row-badges">
          <span className="inbox-chip">{dealStageLabel(event.stage).toUpperCase()}</span>
          {valueLabel ? (
            <span className="inbox-chip accent-ink">{valueLabel}</span>
          ) : (
            <span className="proof-row-muted">No value</span>
          )}
          {event.deadLettered && (
            <span className="inbox-chip danger">DEAD-LETTERED</span>
          )}
        </div>
      </div>
      <OutcomeChips outcomes={event.outcomes} />
    </div>
  );
}

export default function Proof() {
  const { currentUser } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const isMobile = useIsMobile();

  const [events, setEvents] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [logLoading, setLogLoading] = useState(true);
  const [logError, setLogError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [adsStatus, setAdsStatus] = useState(null);
  const [adsLoading, setAdsLoading] = useState(true);

  const [leadSources, setLeadSources] = useState([]);
  const [sourcesTotals, setSourcesTotals] = useState({ leads: 0, revenue: 0 });
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [sourcesError, setSourcesError] = useState(false);

  const fetchLog = useCallback(async (workspaceId, opts = {}) => {
    const res = await trackingApi.getConversionLog(workspaceId, opts);
    if (res?.success === false || res?.error) {
      throw new Error(res?.error || 'Failed to load conversions');
    }
    return {
      events: res.events || [],
      nextBefore: res.nextBefore ?? null,
    };
  }, []);

  const fetchLeadSources = useCallback(async (workspaceId) => {
    const res = await trackingApi.getLeadSources(workspaceId);
    if (res?.success === false || res?.error) {
      throw new Error(res?.error || 'Failed to load lead sources');
    }
    return {
      channels: res.channels || [],
      totals: res.totals || { leads: 0, revenue: 0 },
    };
  }, []);

  const loadInitial = useCallback(async () => {
    if (!currentUser || !selectedWorkspaceId) {
      setEvents([]);
      setNextBefore(null);
      setLogLoading(false);
      setAdsLoading(false);
      setSourcesLoading(false);
      setLeadSources([]);
      setSourcesTotals({ leads: 0, revenue: 0 });
      return;
    }

    setLogLoading(true);
    setLogError(false);
    setAdsLoading(true);
    setSourcesLoading(true);
    setSourcesError(false);
    setEvents([]);
    setNextBefore(null);
    setAdsStatus(null);
    setLeadSources([]);
    setSourcesTotals({ leads: 0, revenue: 0 });

    try {
      const [logResult, adsRes, sourcesResult] = await Promise.all([
        fetchLog(selectedWorkspaceId, { limit: 50 }).then(
          (data) => ({ ok: true, data }),
          () => ({ ok: false })
        ),
        googleAdsApi.status(selectedWorkspaceId),
        fetchLeadSources(selectedWorkspaceId).then(
          (data) => ({ ok: true, data }),
          () => ({ ok: false })
        ),
      ]);

      if (logResult.ok) {
        setEvents(logResult.data.events);
        setNextBefore(logResult.data.nextBefore);
      } else {
        setLogError(true);
        setEvents([]);
        setNextBefore(null);
      }

      if (sourcesResult.ok) {
        setLeadSources(sourcesResult.data.channels);
        setSourcesTotals(sourcesResult.data.totals);
      } else {
        setSourcesError(true);
        setLeadSources([]);
        setSourcesTotals({ leads: 0, revenue: 0 });
      }

      if (adsRes?.error) {
        setAdsStatus(null);
      } else {
        setAdsStatus(adsRes);
      }
    } catch {
      setLogError(true);
      setSourcesError(true);
      setEvents([]);
      setNextBefore(null);
      setLeadSources([]);
      setSourcesTotals({ leads: 0, revenue: 0 });
    } finally {
      setLogLoading(false);
      setAdsLoading(false);
      setSourcesLoading(false);
    }
  }, [currentUser, selectedWorkspaceId, fetchLog, fetchLeadSources]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  async function handleLoadMore() {
    if (!selectedWorkspaceId || nextBefore == null || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetchLog(selectedWorkspaceId, { before: nextBefore, limit: 50 });
      setEvents((prev) => [...prev, ...res.events]);
      setNextBefore(res.nextBefore);
    } catch {
      setLogError(true);
    } finally {
      setLoadingMore(false);
    }
  }

  const pagePad = isMobile ? '1rem 1rem 2rem' : '24px 32px 48px';

  return (
    <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
      <div className="today-page proof-page" style={{ padding: pagePad }}>
        <div className="today-header">
          <div className="today-header-left">
            <h1 className="today-h1">Proof</h1>
          </div>
        </div>

        <ConnectionPanel adsStatus={adsStatus} adsLoading={adsLoading} />

        <LeadSourcesPanel
          channels={leadSources}
          totals={sourcesTotals}
          loading={sourcesLoading}
          error={sourcesError}
          onRefresh={loadInitial}
        />

        <div className="today-panel proof-log-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">Conversions log</span>
          </div>

          {logError && (
            <div className="today-load-error" style={{ margin: '14px 20px 0' }}>
              Could not load conversions. Refresh to retry.{' '}
              <button type="button" className="inbox-pill-btn" onClick={loadInitial} style={{ marginLeft: '8px' }}>
                Refresh
              </button>
            </div>
          )}

          {logLoading ? (
            <ProofListSkeleton />
          ) : !logError && events.length === 0 ? (
            <div className="today-empty">
              No conversions yet. When a deal is marked Won and a conversion fires, it shows up here.
            </div>
          ) : (
            <div className="proof-log-body">
              {events.map((event) => (
                <ConversionRow key={event.id} event={event} />
              ))}
            </div>
          )}

          {!logLoading && !logError && nextBefore != null && (
            <div className="proof-load-more">
              <button
                type="button"
                className="inbox-pill-btn"
                disabled={loadingMore}
                onClick={handleLoadMore}
              >
                {loadingMore ? 'Loading...' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
